/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import {
    ColorAxisOptions,
    IndividualSeriesOptions,
    Options,
    TooltipOptions
} from 'highcharts';
import moment from 'moment';
import datasetTypes from '../../enums/datasetTypes';
import visitMultiAxisDatasetNode from '../../store/util/visitMultiAxisDatasetNode';
import GenericChartVisualization from './chart/GenericChartVisualization';
import timeSeriesChartVisualizationStaticPropertiesReducer from './chart/reducers/timeSeriesChartVisualizationReducer';
import { getEvents, getOutputParamsKeyFromField, getOutputParamFromMeasure } from './chart/util/EventUtil';
import {
    rowsToArrayVisitor,
    getMeasures,
    getTooltipValue
} from './chart/util/GenericSeriesUtil';

const range = (start: number, stop: number, step: number) => {
    if ((step > 0 && start >= stop) || (step < 0 && start <= stop)) {
        return [];
    }

    const result = [];
    for (let i = start; step > 0 ? i < stop : i > stop; i += step) {
        result.push(i);
    }

    return result;
};

const getTimestampFromRowField = (field :any) => {
    // label can be equal to null value, which we treat as 0
    return field.label ? Date.parse(field.label) : 0;
};

export class TimeSeriesHeatMapVisualization extends GenericChartVisualization {

    useShadow = false

    getChartSection() {
        const chart = super.getChartSection();

        return {
            ...chart,
            type: 'heatmap',
            isTimeSeriesHeatMapChart: true
        };
    }

    // eslint-disable-next-line class-methods-use-this
    getSelectedRowIndex(): number {
        return 1;
    }

    getLegendSection() {
        const section = super.getLegendSection();

        delete section.symbolWidth;
        delete section.symbolHeight;
        section.padding = 15;

        return section;
    }

    getColorAxisSection(): ColorAxisOptions {
        return {
            stops: [
                [0.1, '#F6365F'],
                [0.5, '#FFD961'],
                [0.9, '#50C463']
            ],
            labels: {
                style: {
                    fontSize: '1em'
                }
            },
            tickPixelInterval: 150,
            min: this.getRecordedMinMaxValues().min,
            max: this.getRecordedMinMaxValues().max,
        }
    }

    getTooltipSection(): TooltipOptions {
        const section = super.getTooltipSection();
        const staticProperties = this.getStaticProperties();
        const measures = getMeasures(staticProperties.levelsStructure);

        const getTooltipProps = {
            // there is only one measure always, so we don't have to worry about finding the right measure id
            measures,
            defaultFieldAggregationType: this.defaultFieldAggregationType,
            defaultFieldAggregationFormat: this.defaultFieldAggregationFormat
        };

        return {
            ...section,
            backgroundColor: null,
            borderWidth: 0,
            useHTML: true,
            style: {
                padding: 0,
                color: 'black',
                fontSize: '1em'
            },
            formatter() {
                const chartPoint: any = this;
                const { color } = chartPoint.series;
                const measurePrefix = `<span style="color:${color}">\u25CF</span>`;
                const measureName = measures[0].label;
                const time = moment(chartPoint.point.currentTimestamp, 'x').format('D MMM YYYY HH:mm');
                const tooltipValue = getTooltipValue(getTooltipProps, chartPoint.point.value);
                return `${measureName}<br/>${measurePrefix} ${time} <b>${tooltipValue}</b>`;
            }
        };
    }

    getXAxisSection() {
        const selectedRowIndex = this.getSelectedRowIndex();
        const section = super.getXAxisSection(selectedRowIndex);

        return {
            ...section,
            type: 'datetime',
            categories: null,
            endOnTick: false
        }
    }

    getPlotOptionsSection(): any {
        const options = super.getPlotOptionsSection();

        return {
            ...options,
            series: {
                series: {
                    marker: {
                        enabled: true
                    },
                    tooltip: {
                        dateTimeLabelFormats: {
                            millisecond: '%b %e, %Y %H:%M:%S.%L',
                            second: '%b %e, %Y %H:%M:%S',
                            minute: '%b %e, %Y %H:%M',
                            hour: '%b %e, %Y %H:%M',
                            day: '%b %e, %Y %H:%M',
                            week: '%b %e, %Y %H:%M',
                            month: '%b %e, %Y %H:%M',
                            year: '%b %e, %Y %H:%M'
                        },
                        xDateFormat: '%b %e, %Y %H:%M'
                    }
                }
            }
        };
    }

    getSeriesAndAxisSection() {

        const dataset = this.getDataset();
        const staticProperties = this.getStaticProperties();
        const measures = getMeasures(staticProperties.levelsStructure);
        const linkOptions = this.getLinkOptions();

        const columnSeries = this.getColumnSeries();

        const rowsArray = visitMultiAxisDatasetNode(dataset.data.rows, rowsToArrayVisitor, {
            array: [],
            indexes: [],
            stack: [],
            selectedRowIndex: this.getSelectedRowIndex()
        }, true);

        const rowsDataArray = rowsArray.array;
        const colSize = 24 * 36e5;

        let firstMinMaxRecorded :boolean = false,
            min :number = 0,
            max :number = 0;

        let minTimestamp = getTimestampFromRowField(rowsDataArray[0]);
        let maxTimestamp = minTimestamp;
        const timestampToValue: any = {};
        const timeStampToParameters: any = {};

        const result: IndividualSeriesOptions & any[] = [
            {
                borderWidth: 0,
                data: [],
                dataLabels: {
                    enabled: false
                },
                chartType: 'timeseries_heatmap',
                name: columnSeries[0].series.name,
                colsize: colSize,
            }
        ];

        rowsDataArray.forEach((item) => {
            const timestamp = getTimestampFromRowField(item);
            const value = +(dataset.data.aggregations[item.index][0]);

            if (!firstMinMaxRecorded) {
                min = value;
                max = value;
                firstMinMaxRecorded = true;
            } else {
                min = Math.min(value, min);
                max = Math.max(value, max);
            }

            const linkOutputParams: any = {
                measures: [getOutputParamFromMeasure(measures[0])]
            }
            const usedParams = dataset.summary.rows.items;
            linkOutputParams.values = usedParams.map((param: any, parameterIndex: number) => {
                return {
                    key: getOutputParamsKeyFromField(param),
                    value: getTimestampFromRowField(item)
                };
            });
            timeStampToParameters[timestamp] = linkOutputParams;
            timestampToValue[timestamp] = value;
            // find the max and min timestamp
            minTimestamp = Math.min(minTimestamp, timestamp);
            maxTimestamp = Math.max(maxTimestamp, timestamp);
        });
        let currentTimestamp = minTimestamp;

        // also we need a Moment object of that timestamp to get correct hour value form the moment
        // with taking into account the timezone user has selected
        let currentMoment = moment(currentTimestamp, 'x');
        do {
            // let's see if we have a value for this timestamp
            let value;
            if (Object.prototype.hasOwnProperty.call(timestampToValue, currentTimestamp)) {
                value = timestampToValue[currentTimestamp];
            } else {
                value = null;
            }

            // ok, so we have a value, let's grab the current hour
            // this value will be used as Y position on a chart
            const currentHour = currentMoment.hour();

            // to calculate the X position on a chart we need to know the timestamp of the beginning of
            // the currentTimestamp's day.
            // We do this because we need to group all data we have within days
            const timestampAtTheBeginningOfTheDay = currentTimestamp - currentHour * 60 * 60 * 1000;

            // finally, put the data into series object
            let linkOutputParams = timeStampToParameters[currentTimestamp];
            if (!linkOutputParams) {
                linkOutputParams = {
                    measures: [getOutputParamFromMeasure(measures[0])]
                }
            }
            result[0].data.push({
                // for X we use the time floor'ed to the begging of
                x: timestampAtTheBeginningOfTheDay,
                currentTimestamp,
                y: currentHour,
                value,
                linkOutputParams,
            });

            // increase the current timestamp and the current Moment object
            currentMoment.add(1, 'hours');
            currentTimestamp += 60 * 60 * 1000;

        } while (currentTimestamp <= maxTimestamp);

        const yAxis = this.getYAxisSection();
        // Y axis settings
        yAxis.startOnTick = false;
        yAxis.endOnTick = false;
        yAxis.reversed = true;
        yAxis.tickPositioner = function tickPositioner() {
            const step = 1;
            // In this function we need to form an array of mark on Y axis and
            // we don this by getting the lower and the higher hour values and
            // making a range between them.
            // the 'dataMin' and 'dataMax' variables were taken from HC demo
            // there they were used
            const chartPoint: any = this;
            return range(chartPoint.dataMin, chartPoint.dataMax + 1, step);
        };
        yAxis.minRange = 8;
        yAxis.labels!.format = '{value}:00';

        const xAxis = this.getXAxisSection();
        let someMoment = moment(minTimestamp, 'x');
        someMoment.hour(0);
        xAxis.min = parseInt(someMoment.format('x'), 10);

        someMoment = moment(maxTimestamp, 'x');
        someMoment.hour(0);
        xAxis.max = parseInt(someMoment.format('x'), 10);

        xAxis.minRange = 30 * colSize;
        xAxis.labels!.format = '{value:%B %Y}';
        // We want to specify the marks on an X scale because we want
        // to see them with 1 month interval (not a day), so we'll start with
        // the initial time mark we have and we'll step each month.
        // Some more details: before entering the loop we'll add the initial date as it is
        // (which we have from the incoming data), but then we'll drop the current day number to 1
        // and we'll move time ahead for 1 month
        xAxis.tickPositions = [];

        // add initial time point as it is
        xAxis.tickPositions.push(minTimestamp);

        // next, set up the moment object and drop the date to 1 and hours/minutes/seconds to 0
        currentTimestamp = minTimestamp;
        currentMoment = moment(currentTimestamp, 'x');
        currentMoment.day(1);
        currentMoment.hour(0);
        currentMoment.minute(0);
        currentMoment.second(0);
        // next, move it 1 month ahead
        currentMoment.add(1, 'months');
        // and grab the timestamp we got
        currentTimestamp = parseInt(currentMoment.format('x'), 10);

        // and let's add all the rest:
        while (currentTimestamp <= maxTimestamp) {
            xAxis.tickPositions.push(currentTimestamp);
            currentMoment.add(1, 'months');
            currentTimestamp = parseInt(currentMoment.format('x'), 10);
        }

        // attach the events to the series
        result[0].point = {
            events: getEvents(linkOptions),
        }

        this.recordMinMaxValues(min, max);

        return {
            series: result,
            xAxis,
            yAxis,
        }
    }

    getHighchartsOptions() {
        const {
            series,
            xAxis,
            yAxis
        } = this.getSeriesAndAxisSection();

        let highchartsOptions: Options = {
            chart: this.getChartSection(),
            credits: {
                enabled: false
            },
            colors: this.getColorsSection(),
            legend: this.getLegendSection(),
            plotOptions: this.getPlotOptionsSection(),
            series,
            time: {
                useUTC: true
            },
            title: {
                text: null
            },
            tooltip: this.getTooltipSection(),
            xAxis,
            yAxis,
            colorAxis: this.getColorAxisSection()
        };

        highchartsOptions = this.finalOptionAdjustment(highchartsOptions);

        return highchartsOptions;
    }
}

export const TimeSeriesHeatMapVisualizationDescriptor = {
    legacyAdhoc: 'heat_map_time_series',
    cssClass: 'TimeSeriesHeatMap',
    bundleName: 'adhoc.visualisation.chooser.type.TIME_SERIES_HEAT_MAP',
    name: 'TimeSeriesHeatMap',
    group: 'adhoc.visualisation.chooser.type.GROUP_RANGE',
    datasetType: datasetTypes.MULTI_AXIS,
    component: TimeSeriesHeatMapVisualization,
    staticPropertiesReducer: timeSeriesChartVisualizationStaticPropertiesReducer,
    isTimeSeries: true,
    supportsResize: true,
    requirements: {
        measures: {
            min: 1,
            max: 1,
            inRow: false
        },
        fields: {
            min: 1,
            max: 1,
            inColumn: false,
            type: 'time',
            categorizer: ['hour_by_day']
        }
    }
};
